// =============================================================================
// useWebCall — in-browser voice conversation with the tenant's EL ConvAI agent
// =============================================================================
// Lifecycle: idle → connecting → connected (↔ speaking) → ended → idle.
// The hook owns one Conversation instance at a time. Starts a fresh one each
// call so signed URLs (single-use, ~60s expiry) are minted on demand.
//
// The vibe greeting is picked client-side from `lib/vibes.ts` and passed via
// `overrides.agent.firstMessage` — every call sounds a little different, the
// EL agent record is never mutated. See docs/35_CALL_DASHBOARD.md.
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Conversation } from '@elevenlabs/client'
import { apiClient } from './apiClient'
import { effectiveVibe, pickGreeting, type Vibe, type VibePersona } from './vibes'

export type CallStatus =
  | 'idle'
  | 'connecting'
  | 'connected'   // user's turn (agent is listening)
  | 'speaking'   // agent is currently speaking
  | 'ended'
  | 'error'

type StartSessionResult = Awaited<ReturnType<typeof Conversation.startSession>>

interface UseWebCallOptions {
  persona: VibePersona
  vibe: Vibe
  customGreeting?: string | null
}

// Synthesizes a ~150ms confirmation tone on demand (per docs/35 spec) so we
// avoid shipping an audio file. C5 piano-ish: 523Hz sine + a soft fall-off.
function playConnectTone(): void {
  try {
    const AC: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext
    if (!AC) return
    const ctx = new AC()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 523.25 // C5
    const t0 = ctx.currentTime
    gain.gain.setValueAtTime(0.0001, t0)
    gain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.015) // soft attack
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25) // 250ms decay
    osc.connect(gain).connect(ctx.destination)
    osc.start(t0)
    osc.stop(t0 + 0.3)
    osc.onended = () => ctx.close().catch(() => undefined)
  } catch {
    // best-effort — no audio is fine, the call still works
  }
}

// Failure while reaching OUR backend, before we ever got a signed URL (network
// error, 5xx, timeout). Distinct from EL-side failures below — if our API was
// reachable, the problem is not the user's connection to Jarvis.
function toApiError(raw: unknown): string {
  const msg = raw instanceof Error ? raw.message : String(raw ?? '')
  if (/agent not configured|elevenLabsAgentId|not configured/i.test(msg)) {
    return "Voice isn't set up yet — head to Settings to configure it."
  }
  if (/timeout/i.test(msg)) {
    return 'The voice service took too long to respond. Tap the orb to retry.'
  }
  return 'Couldn’t reach Jarvis. Check your connection and tap the orb to retry.'
}

// Failure AFTER our API succeeded — i.e. opening or running the ElevenLabs voice
// WebSocket. `everConnected` separates a setup failure (most often a browser
// ad/privacy blocker blocking api.elevenlabs.io) from a genuine mid-call drop.
function toVoiceError(raw: unknown, everConnected: boolean): string {
  const msg = raw instanceof Error ? raw.message : String(raw ?? '')
  if (/Permission denied|NotAllowedError|denied|getUserMedia|microphone|mic/i.test(msg)) {
    return "Can't hear you — allow microphone access in your browser, then tap to retry."
  }
  if (everConnected) {
    return 'Lost the line. Tap the orb to reconnect.'
  }
  // Our backend was reachable but the EL voice socket never opened. The most
  // common cause is a browser ad/privacy blocker (e.g. Privacy Badger) or a
  // corporate web filter blocking api.elevenlabs.io.
  return 'Couldn’t connect to the voice service. A browser ad/privacy blocker or extension may be blocking api.elevenlabs.io — allow it, then tap to retry.'
}

export function useWebCall(opts: UseWebCallOptions) {
  const [status, setStatus] = useState<CallStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const convRef = useRef<StartSessionResult | null>(null)
  // Did this call ever reach 'connected'? Separates a setup failure from a drop.
  const everConnectedRef = useRef(false)
  // Has a failure already been surfaced for the current attempt? The EL SDK can
  // fire onError/onDisconnect several times for one dead socket; we want exactly
  // one toast per attempt, not the dozen we used to spawn.
  const failedRef = useRef(false)
  // Web-call minute recording: web calls have no ACS disconnect event and the
  // agent's EL post-call webhook isn't attached, so the browser is the only
  // thing that knows the call happened. On disconnect we report the EL
  // conversationId to the backend, which fetches the authoritative duration and
  // deducts minutes. These refs carry the id + connect time + a once-guard.
  const conversationIdRef = useRef<string | null>(null)
  const connectedAtRef = useRef<number | null>(null)
  const reportedRef = useRef(false)

  // Hold the latest opts in a ref so the start() closure always sees current
  // values even if the consumer doesn't memoize them (React re-renders the
  // CommandCenter on every user-doc refresh).
  const optsRef = useRef(opts)
  optsRef.current = opts

  const start = async (): Promise<void> => {
    if (convRef.current || status === 'connecting') return
    setError(null)
    setStatus('connecting')
    everConnectedRef.current = false
    failedRef.current = false
    conversationIdRef.current = null
    connectedAtRef.current = null
    reportedRef.current = false

    // Single sink for any failure in this attempt. De-dupes the EL SDK firing
    // onError/onDisconnect repeatedly for one dead socket (the old toast spam)
    // and tears the session down so it stops retrying.
    const fail = (message: string): void => {
      if (failedRef.current) return
      failedRef.current = true
      const conv = convRef.current
      convRef.current = null
      if (conv) void conv.endSession().catch(() => undefined)
      setError(message)
      setStatus('error')
    }

    // Report a finished web call so its minutes get metered. Fire-and-forget,
    // once per call. The server fetches the authoritative duration from EL; we
    // also pass our measured duration as a fallback for the brief window before
    // EL finalizes the conversation. No conversationId ⇒ nothing to record.
    const reportWebCall = (): void => {
      const conversationId = conversationIdRef.current
      if (!conversationId || reportedRef.current) return
      reportedRef.current = true
      const clientDurationSeconds = connectedAtRef.current
        ? Math.round((Date.now() - connectedAtRef.current) / 1000)
        : undefined
      void apiClient
        .post('/voice/web-call/complete', { conversationId, clientDurationSeconds })
        .catch(() => undefined)
    }

    // Step 1 — ask our backend for a fresh signed URL + the persona-resolved
    // voiceId (server is source of truth; a local persona swap may not have
    // round-tripped yet). A 402 here means out of minutes (docs/47 §4) — show
    // the upgrade/top-up prompt rather than a generic "something went wrong".
    let signedUrl: string
    let voiceId: string | undefined
    try {
      const res = await apiClient.post('/voice/web-call/start')
      const body = res.data as {
        success: boolean
        error?: string
        data?: { signedUrl?: string; voiceId?: string }
      }
      if (!body.success || !body.data?.signedUrl) {
        fail(toApiError(new Error(body.error || 'start failed')))
        return
      }
      signedUrl = body.data.signedUrl
      voiceId = body.data.voiceId
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 402) {
        const data = (e.response.data as { data?: { message?: string } } | undefined)?.data
        fail(
          data?.message ||
            "You're out of voice minutes. Add minutes or enable overage in Settings → Voice Usage.",
        )
        return
      }
      fail(toApiError(e))
      return
    }

    // Step 2 — open the ElevenLabs voice socket. Any failure from here on is
    // EL-side (blocked socket, mic, or a mid-call drop), mapped by toVoiceError.
    try {
      const { persona, vibe, customGreeting } = optsRef.current
      const resolvedVibe = effectiveVibe(persona, vibe)
      const greeting = pickGreeting(persona, resolvedVibe, customGreeting)

      const conv = await Conversation.startSession({
        signedUrl,
        overrides: {
          agent: { firstMessage: greeting },
          // Per-call voice swap. When the user toggles to ULTRON the next
          // call dynamically uses the Ultron voiceId without us having to
          // mutate the underlying EL agent record. Skipped silently if
          // server didn't return one (older bundle / agent not provisioned).
          ...(voiceId ? { tts: { voiceId } } : {}),
        },
        onConnect: () => {
          everConnectedRef.current = true
          connectedAtRef.current = Date.now()
          playConnectTone()
          // Initial state is "user's turn" — agent will fire onModeChange
          // when it starts speaking the greeting.
          setStatus('connected')
        },
        onDisconnect: () => {
          convRef.current = null
          // A disconnect before we ever connected is a setup failure (e.g. a
          // blocked socket), not a normal hang-up — surface it as an error.
          if (!everConnectedRef.current) {
            fail(toVoiceError(new Error('socket closed before connect'), false))
            return
          }
          // A real call just ended — record its minutes (web has no ACS path).
          reportWebCall()
          setStatus('ended')
          // Auto-fade back to idle after a short pause so the orb settles.
          window.setTimeout(() => {
            // Only flip back to idle if no new call was started in the meantime
            setStatus((s) => (s === 'ended' ? 'idle' : s))
          }, 4000)
        },
        onError: (message) => {
          fail(toVoiceError(message, everConnectedRef.current))
        },
        onModeChange: ({ mode }) => {
          // EL Mode is 'speaking' (agent is talking) or 'listening' (user's turn)
          setStatus(mode === 'speaking' ? 'speaking' : 'connected')
        },
      })

      // If a failure already fired while startSession was resolving, drop the
      // handle so the SDK doesn't keep the mic/socket open behind our backs.
      if (failedRef.current) {
        void conv.endSession().catch(() => undefined)
        return
      }
      convRef.current = conv
      // Capture the EL conversation id now that the session is live — needed to
      // record minutes on disconnect. getId() is available once connected; guard
      // for older SDKs that may not expose it.
      try {
        const id = (conv as unknown as { getId?: () => string }).getId?.()
        if (id) conversationIdRef.current = id
      } catch {
        /* no id available — minutes for this call simply won't be recorded */
      }
    } catch (e) {
      fail(toVoiceError(e, everConnectedRef.current))
    }
  }

  const end = async (): Promise<void> => {
    const conv = convRef.current
    convRef.current = null
    if (conv) {
      try {
        await conv.endSession()
      } catch {
        // already closed — ignore
      }
    }
    setStatus('idle')
  }

  // Hang up on unmount so a navigation away cleanly closes the WebSocket
  // (otherwise the mic stays open and EL keeps billing for it).
  useEffect(() => {
    return () => {
      const conv = convRef.current
      convRef.current = null
      if (conv) {
        void conv.endSession().catch(() => undefined)
      }
    }
  }, [])

  const isActive = status === 'connected' || status === 'speaking'

  return { status, error, isActive, start, end }
}

export type UseWebCallReturn = ReturnType<typeof useWebCall>
